import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronUp, Home, Plus, Users } from "lucide-react";
import { NewClientDialog } from "@/components/NewClientDialog";
import { AddMemberDialog, MemberRow } from "@/components/HouseholdCard";
import { useDemo } from "@/store/DemoStore";
import { Avatar, Card, Empty, PageHeader, SearchInput, btn } from "@/components/common";
import { daysUntil, fmtDate } from "@/lib/trip";

function ClientRow({ c, members, tripsFor }) {
  const [open, setOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const isHead = members.length > 0;
  const trips = tripsFor(c.id);
  const next = trips.filter((t) => t.status === "BOOKED" && daysUntil(t.departure) >= 0).sort((a, b) => a.departure.localeCompare(b.departure))[0];
  return (
    <li>
      <div className="flex items-center gap-2 transition-colors hover:bg-slate-50">
        <Link to={`/agent/clients/${c.id}`} data-testid={`client-row-${c.id}`} className="grid min-w-0 flex-1 grid-cols-[1fr] items-center gap-4 px-4 py-4 sm:pl-6 md:grid-cols-[2fr_2fr_0.7fr_1.2fr]">
          <div className="flex min-w-0 items-center gap-3">
            <Avatar name={c.name} />
            <div className="flex min-w-0 items-center gap-1.5">
              <p className="truncate text-sm font-semibold text-navy-900">{c.name}</p>
              {isHead && <Home className="h-3.5 w-3.5 shrink-0 text-navy-400" />}
            </div>
          </div>
          <div className="hidden min-w-0 md:block">
            <p className="truncate text-sm text-navy-900">{c.email}</p>
            <p className="text-xs text-slate-500">{c.phone}</p>
          </div>
          <span className="hidden text-sm text-navy-900 md:block">{trips.length}</span>
          <span className="hidden text-sm text-navy-900 md:block">{next ? fmtDate(next.departure) : <span className="text-slate-400">—</span>}</span>
        </Link>
        {isHead ? (
          <button onClick={() => setOpen((v) => !v)} data-testid={`household-toggle-${c.id}`} title="Household members" className="mr-3 grid h-9 w-9 shrink-0 place-items-center rounded-full text-navy-600 transition-colors hover:bg-navy-50 sm:mr-4">{open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}</button>
        ) : (
          <button onClick={() => setAddOpen(true)} data-testid={`start-household-${c.id}`} title="Start a household" className="mr-3 grid h-9 w-9 shrink-0 place-items-center rounded-full text-slate-400 transition-colors hover:bg-navy-50 hover:text-navy-700 sm:mr-4"><Home className="h-4 w-4" /></button>
        )}
      </div>
      {isHead && open && (
        <div className="border-t border-slate-100 bg-slate-50/60 px-4 py-4 sm:px-6">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Household members</span>
            <button onClick={() => setAddOpen(true)} data-testid={`household-add-member-${c.id}`} className="inline-flex items-center gap-1 text-sm font-semibold text-navy-900 transition-colors hover:text-navy-700"><Plus className="h-4 w-4" />Add Member</button>
          </div>
          <div className="space-y-2">{members.map((m) => <MemberRow key={m.id} m={m} />)}</div>
        </div>
      )}
      <AddMemberDialog head={c} open={addOpen} onOpenChange={setAddOpen} />
    </li>
  );
}

export default function ClientsPage() {
  const { clients, tripsFor } = useDemo();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const s = q.toLowerCase();
  const match = (c) => [c.name, c.email, c.phone].join(" ").toLowerCase().includes(s);

  const households = {};
  clients.forEach((c) => {
    if (!c.householdId) return;
    const h = (households[c.householdId] ||= { members: [] });
    if (c.householdPrimary) h.primary = c; else h.members.push(c);
  });
  const active = Object.values(households).filter((h) => h.primary && h.members.length);
  const memberIds = new Set(active.flatMap((h) => h.members.map((m) => m.id)));
  const membersByPrimary = Object.fromEntries(active.map((h) => [h.primary.id, h.members]));
  const rows = clients.filter((c) => !memberIds.has(c.id)).filter((c) => match(c) || (membersByPrimary[c.id] || []).some(match));

  return (
    <div>
      <PageHeader title="Clients" subtitle={`${clients.length} clients · ${clients.filter((c) => c.status === "active").length} on MyTrip`} actions={<button onClick={() => setOpen(true)} data-testid="new-client-btn" className={btn.primary}><Plus className="h-4 w-4" />Add Client</button>} />
      {open && <NewClientDialog open onOpenChange={setOpen} />}
      <div className="mb-5">
        <SearchInput value={q} onChange={setQ} placeholder="Search by name, email or phone" testId="clients-search-input" className="md:max-w-md" />
      </div>
      <Card className="overflow-hidden">
        <div className="hidden grid-cols-[2fr_2fr_0.7fr_1.2fr_52px] gap-4 border-b border-slate-100 px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400 md:grid">
          <span>Name</span><span>Contact</span><span>Trips</span><span>Next departure</span><span />
        </div>
        <ul className="divide-y divide-slate-100" data-testid="clients-list">
          {rows.map((c) => <ClientRow key={c.id} c={c} members={membersByPrimary[c.id] || []} tripsFor={tripsFor} />)}
        </ul>
        {!rows.length && <Empty icon={Users} title="No clients match" text="Try a different search or add a client." />}
      </Card>
    </div>
  );
}
